<script setup>
import { ref, onMounted, watch } from 'vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import CampaignsAPI from 'dashboard/api/campaigns';
import CampaignCard from 'dashboard/components-next/Campaigns/CampaignCard/CampaignCard.vue';

const props = defineProps({
  campaigns: {
    type: Array,
    required: true,
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['edit', 'delete', 'archive', 'unarchive']);

// NOTICE: KAPTION-INTEGRATION
// Store campaign stats indexed by campaign ID
const campaignStats = ref({});

const handleEdit = campaign => emit('edit', campaign);
const handleDelete = campaign => emit('delete', campaign);
const handleArchive = campaign => emit('archive', campaign);
const handleUnarchive = campaign => emit('unarchive', campaign);

// NOTICE: KAPTION-INTEGRATION
// Fetch stats for WhatsApp campaigns
const fetchCampaignStats = async () => {
  if (props.isLiveChatType) return;

  const whatsappCampaigns = props.campaigns.filter(
    campaign => campaign.inbox?.channel_type === INBOX_TYPES.WHATSAPP
  );

  // Fetch stats for each WhatsApp campaign
  const statsPromises = whatsappCampaigns.map(async campaign => {
    try {
      const response = await CampaignsAPI.getStatus(campaign.id);
      if (response.data?.success && response.data?.counters) {
        campaignStats.value[campaign.id] = response.data.counters;
      }
    } catch (error) {
      // Silently fail - stats are optional
      // Error handling: stats failures don't break campaign display
    }
  });

  await Promise.all(statsPromises);
};

// Fetch stats when campaigns change
watch(
  () => props.campaigns,
  () => {
    fetchCampaignStats();
  },
  { immediate: true }
);

// Fetch stats on mount
onMounted(() => {
  fetchCampaignStats();
});
</script>

<template>
  <div class="wp-row-list flex flex-col gap-4">
    <CampaignCard
      v-for="campaign in campaigns"
      :id="campaign.id"
      :key="campaign.id"
      :title="campaign.title"
      :message="campaign.message"
      :is-enabled="campaign.enabled"
      :status="campaign.campaign_status"
      :sender="campaign.sender"
      :inbox="campaign.inbox"
      :scheduled-at="campaign.scheduled_at"
      :is-live-chat-type="isLiveChatType"
      :stats="
        campaignStats[campaign.id] || {
          sent: 0,
          delivered: 0,
          read: 0,
          replied: 0,
          failed: 0,
        }
      "
      @edit="handleEdit(campaign)"
      @delete="handleDelete(campaign)"
      @archive="handleArchive(campaign)"
      @unarchive="handleUnarchive(campaign)"
    />
  </div>
</template>
